import {Card} from "@/components/card/Card.tsx";
import {LocationResponseTimes} from "@/lib/queries/checks.ts";
import {MonitoringGroup} from "@/lib/types/monitoring.ts";
import {ResponseTimeAreaChart} from "@/pages/checks/analysis/overview/ResponseTimeAreaChart.tsx";
import {ResponseTimeStatsCards} from "@/pages/checks/analysis/overview/ResponseTimeStatsCards.tsx";
import type {MinMaxAvg} from "@/pages/checks/analysis/overview/utils.ts";
import {formatISO} from "date-fns";
import {useMemo} from "react";
import {FiZoomIn} from "react-icons/fi";

import type {ComponentDrilldownResult} from "../../types.ts";
import styles from "./DrilldownResponseTimeChart.module.scss";

interface DrilldownResponseTimeChartProps {
  drilldown: ComponentDrilldownResult;
  start: Date;
  end: Date;
  timezone?: string;
}

const buildChartInputs = (
  drilldown: ComponentDrilldownResult
): {
  responseTimes: LocationResponseTimes[];
  locations: Pick<MonitoringGroup, "id" | "location">[];
} => {
  const series = drilldown.response_time_data ?? [];
  const responseTimes: LocationResponseTimes[] = series.map((loc, idx) => ({
    id: idx + 1,
    data: loc.data.map(([ts, val]) => ({
      timestamp: formatISO(new Date(ts * 1000)),
      value: val,
    })),
  }));

  const locations: Pick<MonitoringGroup, "id" | "location">[] = series.map((loc, idx) => ({
    id: idx + 1,
    location: loc.location,
  }));

  return {responseTimes, locations};
};

// The section renders whether or not the check reported anything: the axis is the selected range,
// so an empty period draws an empty chart over that period instead of the section vanishing.
export const DrilldownResponseTimeChart = ({drilldown, start, end, timezone}: DrilldownResponseTimeChartProps) => {
  const {responseTimes, locations} = useMemo(() => buildChartInputs(drilldown), [drilldown]);

  const stats: MinMaxAvg = {
    max: drilldown.response_time_high ?? 0,
    min: drilldown.response_time_low ?? 0,
    avg: drilldown.response_time_avg ?? 0,
  };

  return (
    <Card variant="white" className={styles.responseTimeSection}>
      <div className={styles.responseTimeHeader}>
        <span className={styles.sectionTitle}>Response Time</span>
        <div className={styles.zoomHint}>
          <FiZoomIn size={16} />
          <span>Drag chart to zoom</span>
        </div>
      </div>
      <div className={styles.chartContainer}>
        {/* Markers left on deliberately — the design draws one per point, and the count is bounded:
            the backend downsamples to ~90 buckets whatever the window (mdb/query/tsdb.py:571). */}
        <ResponseTimeAreaChart
          start={start}
          end={end}
          timezone={timezone}
          responseTimes={responseTimes}
          locations={locations}
          alerts={[]}
          onLocationClick={() => {}}
          onRangeChange={() => {}}
        />
      </div>
      <ResponseTimeStatsCards stats={stats} />
    </Card>
  );
};
